import { motion } from "framer-motion";
import { Check, Crown, Sparkle } from "@phosphor-icons/react";
import { Label } from "../../components/ui/label";
import { svcApplies, usd } from "./booking-shared";

/* ---------- Step: Package ---------- */
export function StepPackage({ cfg, o, r, pickPackage, toggleWaive, toggleItem, toggleAddon }) {
  const isLand = o.property_type === "land";
  const penthousePrice = o.entry ? cfg.packages.all_inclusive : cfg.packages.no_entry;
  const services = (cfg.services || []).filter((s) => svcApplies(cfg, s.key, o.property_type));
  const aiLabel = cfg.all_inclusive_label || "All-Inclusive";
  const aiImage = cfg.all_inclusive_image || "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
  const _rawIncl = (cfg.all_inclusive_inclusions && cfg.all_inclusive_inclusions.length)
    ? cfg.all_inclusive_inclusions
    : [
        { title: "Interior & exterior photos", desc: "30–50 HDR images, fully edited" },
        { title: "Cinematic video", desc: "Branded + unbranded MLS cut" },
        { title: "Virtual staging", desc: "5 styled photos in your chosen look" },
        { title: "360° virtual tour", desc: "Interactive walkthrough of the full space" },
        { title: "Presentation Generator + LeadLander + ListAssist", desc: "Listing presentation deck + property landing page + AI listing copy" },
      ];
  const _incl = _rawIncl.map((i) => (typeof i === "string" ? { title: i, desc: "" } : { title: i.title || i.label || "", desc: i.desc || "" }));
  const _isPenthouse = (t) => /360|leadlander|listassist/i.test(t);
  const inclusions = [..._incl].sort((a, b) => (_isPenthouse(a.title) ? 1 : 0) - (_isPenthouse(b.title) ? 1 : 0));
  const inclSqft = Math.round(cfg.oversized?.auto_sqft || 5000);
  const inclAcre = Math.round(cfg.oversized?.auto_acre || 50);
  const oversizedPrice = Math.round(cfg.oversized?.price || 197);
  return (
    <div data-testid="step-package">
      <h2 className="font-display text-2xl sm:text-3xl text-[#4A0000] tracking-tight mb-6">Choose your experience</h2>

      {!isLand && (
        <div className="grid sm:grid-cols-2 gap-3.5">
          {/* All-Inclusive (hero) */}
          <motion.div data-testid="pkg-all" role="button" tabIndex={0} onClick={() => pickPackage("all_inclusive")}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && pickPackage("all_inclusive")}
            whileHover={{ y: -4 }} whileTap={{ scale: 0.99 }} transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className={`relative overflow-hidden cursor-pointer text-left rounded-2xl p-5 sm:p-6 border transition-colors ${o.package === "all_inclusive"
              ? "border-[#B08D57] bg-gradient-to-b from-[#FBF6EA] to-[#F6EEDB] shadow-[0_18px_50px_-18px_rgba(74,0,0,0.4)]"
              : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0] shadow-[0_10px_30px_-20px_rgba(74,0,0,0.3)]"}`}>
            <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#B08D57]/15 blur-3xl" />
            {aiImage && (
              <div className="-mx-5 -mt-5 sm:-mx-6 sm:-mt-6 mb-4 relative h-36 sm:h-40 overflow-hidden">
                <img src={aiImage} alt={aiLabel} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A0A0A]/45 via-transparent to-transparent" />
              </div>
            )}
            <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] bg-[#4A0000] text-[#FBF6EA] rounded-full px-2.5 py-1 shadow-sm z-10">
              Most Popular
            </span>
            <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A6D1B] font-semibold">Signature</p>
            <p className="font-display text-2xl text-[#4A0000] mt-1 leading-tight">{aiLabel}</p>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="font-mono text-3xl text-[#1A1715] tabular-nums">{usd(penthousePrice)}</span>
              <span className="text-xs text-[#8A8782]">flat · one visit</span>
            </div>
            <p className="mt-2 text-[10px] sm:text-[11px] tracking-[0.02em] text-[#8A6D1B] whitespace-nowrap" data-testid="size-included-note">
              Properties up to <b className="font-semibold text-[#4A0000]">{inclSqft.toLocaleString()} sq ft</b> or <b className="font-semibold text-[#4A0000]">{inclAcre} acres</b> included
            </p>
            <p className="mt-0.5 text-[9px] sm:text-[10px] text-[#8A8782]" data-testid="size-oversized-note">
              (Get unlimited sq ft / acreage for just {usd(oversizedPrice)})
            </p>
            {o.package === "all_inclusive" ? (
            <>
            <ul className="mt-4 space-y-2 text-sm text-[#4A3F3A] w-full">
              {inclusions.map((inc, i) => {
                const waived = o.waive_extras && /360/i.test(inc.title);
                const penthouse = _isPenthouse(inc.title);
                return (
                <motion.li key={inc.title || i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.05 }}
                  className="flex items-start gap-2 leading-snug" data-testid={penthouse ? "penthouse-perk" : undefined}>
                  <span className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${waived ? "bg-[#8A8782]/12" : penthouse ? "bg-[#B08D57]/18" : "bg-[#2E7D5B]/12"}`}>
                    {penthouse
                      ? <Crown size={11} weight="fill" className={waived ? "text-[#8A8782]" : "text-[#B08D57]"} />
                      : <Check size={11} weight="bold" className="text-[#2E7D5B]" />}
                  </span>
                  <span className={`min-w-0 flex-1 transition-opacity ${waived ? "line-through opacity-50" : ""}`}>
                    <span className="font-medium text-[#1A1715] break-words">
                      {inc.title}
                      {penthouse && !waived && <span className="ml-1.5 relative -top-px align-middle whitespace-nowrap inline-block leading-none text-[9px] font-bold uppercase tracking-[0.1em] text-[#FBF6EA] bg-[#B08D57] rounded px-1 py-0.5">Penthouse Partner</span>}
                    </span>
                    {inc.desc && <span className="block text-[11px] text-[#8A8782] leading-snug mt-0.5 break-words">{inc.desc}</span>}
                  </span>
                </motion.li>
                );
              })}
            </ul>
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-[#B08D57]/40 bg-gradient-to-r from-[#FBF6EA] to-[#F6EEDB] px-3 py-2" data-testid="penthouse-free-note">
              <Crown size={14} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
              <p className="text-[11px] leading-snug text-[#7a5c1a]">
                <b className="text-[#4A0000] block">Penthouse Partner perks included</b>
                <span className="block mt-0.5">First month free, then your entire first year is just <span className="font-mono">$45</span>.</span>
              </p>
            </div>
            </>
            ) : (
              <p className="mt-3 text-[13px] text-[#4A3F3A] leading-snug" data-testid="pkg-all-condensed">
                Photos + cinematic video + virtual staging + a 360° tour — plus Penthouse perks, all in one visit. <span className="text-[#8A6D1B] font-medium">Tap to see everything included.</span>
              </p>
            )}
          </motion.div>
          {/* Build your own */}
          <motion.div data-testid="pkg-alacarte" role="button" tabIndex={0} onClick={() => pickPackage("a_la_carte")}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && pickPackage("a_la_carte")}
            whileHover={{ y: -4 }} whileTap={{ scale: 0.99 }} transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className={`relative cursor-pointer text-left rounded-2xl p-5 sm:p-6 border transition-colors ${o.package === "a_la_carte" ? "border-[#4A0000] bg-[#4A0000]/[0.04] shadow-[0_14px_40px_-20px_rgba(74,0,0,0.45)]" : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0]"}`}>
            <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A8782] font-semibold">Flexible</p>
            <p className="font-display text-2xl text-[#4A0000] mt-1 leading-tight">Build your own</p>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="font-mono text-3xl text-[#1A1715] tabular-nums">{o.package === "a_la_carte" ? usd(r.package_price) : "À la carte"}</span>
            </div>
            {o.package === "a_la_carte" ? (
            <ul className="mt-4 space-y-2 text-sm text-[#4A3F3A] w-full">
              {["Pick only the services you want", "Add drone photo &/or video", "360° tour optional", "Pay for exactly what you pick"].map((f) => (
                <li key={f} className="flex items-start gap-2 leading-snug"><span className="mt-0.5 h-4 w-4 rounded-full bg-[#B08D57]/15 flex items-center justify-center shrink-0"><Check size={11} weight="bold" className="text-[#B08D57]" /></span><span className="min-w-0 flex-1 whitespace-normal break-words">{f}</span></li>
              ))}
            </ul>
            ) : (
              <p className="mt-3 text-sm text-[#4A3F3A] leading-snug" data-testid="pkg-alacarte-condensed">
                Pick only the services you want and pay for exactly what you choose. <span className="text-[#8A6D1B] font-medium">Tap to build your shoot.</span>
              </p>
            )}
          </motion.div>
        </div>
      )}

      {/* À la carte builder */}
      {o.package === "a_la_carte" && (
        <div className="mt-5" data-testid="alacarte-builder">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#8A6D1B] font-semibold">{isLand ? "Drone services" : "Choose your line items"}</Label>
          <div className="grid sm:grid-cols-2 gap-2.5 mt-2">
            {services.map((s) => {
              const on = (o.items || []).includes(s.key);
              const mandatory = isLand && s.key === "drone_photo";
              const dronePhotoPrice = (cfg.services || []).find((x) => x.key === "drone_photo")?.price || 0;
              const isLandVideo = isLand && s.key === "drone_video";
              const shownPrice = isLandVideo ? Math.max(0, s.price - dronePhotoPrice) : s.price;
              return (
                <motion.button key={s.key} data-testid={`alacarte-${s.key}`} onClick={() => (mandatory ? null : toggleItem(s.key))}
                  aria-disabled={mandatory} whileHover={mandatory ? undefined : { y: -2 }} whileTap={mandatory ? undefined : { scale: 0.985 }}
                  transition={{ type: "spring", stiffness: 340, damping: 24 }}
                  className={`group relative flex items-start gap-3 rounded-xl border p-3.5 text-left min-w-0 transition-colors ${mandatory ? "cursor-default" : ""} ${on
                    ? "border-[#B08D57] bg-gradient-to-b from-[#FBF6EA] to-[#F6EEDB] shadow-[0_10px_28px_-16px_rgba(74,0,0,0.35)]"
                    : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0] shadow-[0_6px_18px_-14px_rgba(74,0,0,0.3)]"}`}>
                  <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 border transition-colors ${on ? "bg-[#2E7D5B] border-[#2E7D5B] text-white" : "bg-white border-[#D9CFB0] text-transparent group-hover:border-[#B08D57]"}`}>
                    <Check size={12} weight="bold" />
                  </span>
                  <span className="min-w-0 flex-1 pr-2">
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-semibold text-[#1A1715] leading-tight">
                      <span className="break-words min-w-0">{s.label}</span>
                      {mandatory && <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.1em] text-[#2E7D5B] bg-[#2E7D5B]/12 rounded px-1 py-0.5">Included</span>}
                    </span>
                    <span className="block text-[11px] text-[#8A8782] leading-snug mt-0.5 break-words">{isLandVideo ? "Add cinematic aerial flyover video" : s.desc}</span>
                  </span>
                  <span className={`font-mono text-sm shrink-0 tabular-nums ${on ? "text-[#4A0000]" : "text-[#5C5A58]"}`}>{isLandVideo ? `+${usd(shownPrice)}` : usd(shownPrice)}</span>
                </motion.button>
              );
            })}
          </div>
          {o.package === "a_la_carte" && (!o.items || o.items.length === 0) && (
            <p className="text-xs text-[#B4472E] mt-2 flex items-center gap-1"><Sparkle size={12} weight="fill" />Select at least one service to continue.</p>
          )}
        </div>
      )}

      {/* All-Inclusive drone add-ons (paid; photo or video = $279 each, both bundle to $360) */}
      {!isLand && o.package === "all_inclusive" && (
        <div className="mt-4" data-testid="drone-addons">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#8A6D1B] font-semibold">Add drone aerials <span className="normal-case tracking-normal text-[#8A8782] font-normal">· optional</span></Label>
          <div className="grid sm:grid-cols-2 gap-2.5 mt-2">
            {[{ key: "drone_photo", label: "Add drone photos", desc: "Aerial stills of the property & lot" },
              { key: "drone_video", label: "Add drone video", desc: "Cinematic aerial flyover video" }].map((d) => {
              const on = (o.addons || []).includes(d.key);
              const single = (cfg.services || []).find((x) => x.key === "drone_photo")?.price || 279;
              const bundle = (cfg.services || []).find((x) => x.key === "drone_video")?.price || 360;
              const otherOn = (o.addons || []).includes(d.key === "drone_photo" ? "drone_video" : "drone_photo");
              const price = otherOn ? Math.max(0, bundle - single) : single;
              const bundleUpsell = !on && otherOn; // the other is picked → nudge them to complete the bundle
              return (
                <motion.button key={d.key} data-testid={`addon-${d.key}`} onClick={() => toggleAddon(d.key)}
                  whileHover={{ y: -2 }} whileTap={{ scale: 0.985 }} transition={{ type: "spring", stiffness: 340, damping: 24 }}
                  animate={bundleUpsell ? { boxShadow: ["0 0 0 0 rgba(176,141,87,0.0)", "0 0 0 4px rgba(176,141,87,0.28)", "0 0 0 0 rgba(176,141,87,0.0)"] } : {}}
                  {...(bundleUpsell ? { transition: { duration: 1.6, repeat: Infinity, ease: "easeInOut" } } : {})}
                  className={`group relative flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${on
                    ? "border-[#B08D57] bg-gradient-to-b from-[#FBF6EA] to-[#F6EEDB] shadow-[0_10px_28px_-16px_rgba(74,0,0,0.35)]"
                    : bundleUpsell
                    ? "border-[#B08D57] bg-gradient-to-b from-[#FFFBF1] to-[#FBF3DF]"
                    : "border-[#E9DDC6] bg-white hover:border-[#D9CFB0] shadow-[0_6px_18px_-14px_rgba(74,0,0,0.3)]"}`}>
                  {bundleUpsell && (
                    <span className="absolute -top-2 left-3 z-10 flex items-center gap-1 rounded-full bg-[#4A0000] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-[#FBF6EA] shadow-sm">
                      <Sparkle size={9} weight="fill" className="text-[#E6C687]" />Save {usd(single - price)}
                    </span>
                  )}
                  <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 border transition-colors ${on ? "bg-[#2E7D5B] border-[#2E7D5B] text-white" : bundleUpsell ? "bg-white border-[#B08D57] text-transparent" : "bg-white border-[#D9CFB0] text-transparent group-hover:border-[#B08D57]"}`}>
                    <Check size={12} weight="bold" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1A1715] leading-tight">{d.label}</span>
                    <span className="block text-[11px] leading-snug mt-0.5 break-words">
                      {bundleUpsell
                        ? <span className="text-[#8A6D1B] font-semibold">Complete your bundle — add for just {usd(price)}</span>
                        : <span className="text-[#8A8782]">{d.desc}</span>}
                    </span>
                  </span>
                  <span className={`font-mono text-sm shrink-0 tabular-nums ${on ? "text-[#4A0000]" : bundleUpsell ? "text-[#4A0000] font-semibold" : "text-[#5C5A58]"}`}>
                    {bundleUpsell && <span className="line-through text-[#B0A99A] text-xs mr-1 font-normal">{usd(single)}</span>}+{usd(price)}
                  </span>
                </motion.button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#8A6D1B] mt-1.5 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" className="text-[#B08D57]" />Add both photo &amp; video for just {usd(360)} total.
          </p>
        </div>
      )}

      {/* All-Inclusive: the 360° tour is OFF by default so the extra 25% shows first;
          the customer opts IN to add the tour (which uses up that 25%). */}
      {!isLand && o.package === "all_inclusive" && (
        <motion.button data-testid="waive-toggle" onClick={toggleWaive}
          whileTap={{ scale: 0.99 }}
          className={`mt-3 w-full flex items-center gap-3 rounded-xl border p-3.5 text-left whitespace-normal break-words transition-colors ${!o.waive_extras
            ? "border-[#B08D57]/60 bg-[#FBF6EA]"
            : "border-dashed border-[#2E7D5B]/50 bg-[#EEF6F1]/50 hover:bg-[#EEF6F1]"}`}>
          <span className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 border transition-colors ${!o.waive_extras ? "bg-[#B08D57] border-[#B08D57] text-white" : "bg-white border-[#D9CFB0] text-transparent"}`}>
            <Check size={12} weight="bold" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[#1A1715] leading-tight">Add the 360° virtual tour</span>
            <span className="block text-[11px] text-[#8A8782] leading-snug mt-0.5">
              {!o.waive_extras
                ? "Added — this uses your extra 25% off. Untick to drop the tour and keep 25% off."
                : "Leave it off and keep an extra 25% off the package. Add it anytime."}
            </span>
          </span>
          <span className={`font-mono text-sm shrink-0 tabular-nums ${!o.waive_extras ? "text-[#B08D57]" : "text-[#2E7D5B]"}`}>
            {!o.waive_extras ? "+360°" : "−25%"}
          </span>
        </motion.button>
      )}
    </div>
  );
}
